/*
 * My picture and color (owner, 2026-10-30): anyone on the team picks a profile picture and the color of their circle. The
 * picture shows in their circle on the schedule and team with a thin ring of the color; without one, their initials sit on
 * it. The picture is cut to a small square here before it goes up, so it loads fast everywhere.
 */
import { PERSON_COLORS, personColor } from "@steward/shared";
import { useRef, useState } from "react";
import { useMyPerson, useSetMyColor, useSetMyPhoto } from "../api/hooks";
import { crewWords, useSession } from "../app/session";
import { Modal, useToast } from "./overlays";
import { Avatar, Button, Skeleton } from "./parts";
import { errText } from "./work";

const SIDE = 320;
const COLOR_NAMES = ["Blue", "Green", "Orange", "Purple", "Cyan", "Pink", "Indigo", "Mint", "Gold", "Brown", "Teal"];

/** The middle square of a picture, made SIDE pixels and saved as a JPG. */
async function squareJpeg(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const s = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement("canvas");
  canvas.width = SIDE;
  canvas.height = SIDE;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no canvas");
  ctx.drawImage(bitmap, (bitmap.width - s) / 2, (bitmap.height - s) / 2, s, s, 0, 0, SIDE, SIDE);
  bitmap.close();
  return canvas.toDataURL("image/jpeg", 0.86).split(",")[1] ?? "";
}

export function ProfilePhotoModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { company, me: who } = useSession();
  const words = crewWords(who.locale);
  const me = useMyPerson(company.id);
  const setColor = useSetMyColor();
  const setPhoto = useSetMyPhoto();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const p = me.data;

  const pick = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast(words.notAPicture, { kind: "error" });
      return;
    }
    setBusy(true);
    try {
      const dataBase64 = await squareJpeg(file);
      await setPhoto.mutateAsync({ name: "photo.jpg", mime: "image/jpeg", dataBase64 });
      toast(words.pictureSaved);
    } catch (e) {
      toast(errText(e, words.pictureFailed), { kind: "error" });
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  return (
    <Modal title={words.pictureAndColor} open={open} onClose={onClose}>
      {me.isLoading ? (
        <Skeleton rows={3} />
      ) : !p ? (
        <p className="muted">{words.notOnTeam}</p>
      ) : (
        <div className="stack profile-photo">
          <div className="row">
            <Avatar name={p.name} person={p} size="large" />
            <div className="stack tight">
              <b>{p.name}</b>
              <span className="muted small">{words.pictureWhere}</span>
            </div>
          </div>
          <div className="row tight">
            <Button type="secondary" size="small" icon="camera" loading={busy} onClick={() => input.current?.click()}>
              {p.photo ? words.changePicture : words.addPicture}
            </Button>
            {p.photo ? (
              <Button
                type="tertiary"
                variation="destructive"
                size="small"
                disabled={busy}
                onClick={() =>
                  setPhoto.mutate(null, {
                    onSuccess: () => toast(words.pictureOff),
                    onError: (e) => toast(errText(e), { kind: "error" }),
                  })
                }
              >
                {words.takePictureOff}
              </Button>
            ) : null}
            <input
              ref={input}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              hidden
              onChange={(e) => void pick(e.target.files?.[0])}
            />
          </div>
          <div className="stack tight">
            <b className="small">{words.yourColor}</b>
            <div className="swatches" role="radiogroup" aria-label={words.yourColor}>
              {PERSON_COLORS.map((c, i) => {
                const on = personColor(p).toLowerCase() === c.toLowerCase();
                return (
                  <button
                    key={c}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    aria-label={COLOR_NAMES[i] ?? c}
                    title={COLOR_NAMES[i]}
                    className={`swatch-pick${on ? " on" : ""}`}
                    style={{ background: c }}
                    onClick={() => setColor.mutate(c, { onError: (e) => toast(errText(e), { kind: "error" }) })}
                  />
                );
              })}
            </div>
            <span className="muted small">{words.ringNote}</span>
          </div>
        </div>
      )}
    </Modal>
  );
}
